import React, { useState, useEffect, useMemo } from "react";
import imageOne from "../../resource/events/notification/1.png";
import imageTwo from "../../resource/events/notification/3.png";
import barrelBronze from "../../resource/events/notification/barrel_bronze.png";
import barrelBlack from "../../resource/events/notification/barrel_black.png";
import barrelSilver from "../../resource/events/notification/barrel_silver.png";
import TitleChnage from "../../Hook/TitleChnage";
import apiClient from "@/api/client";

const EventNotification = () => {
  TitleChnage("Event Notification");

  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeArchive, setActiveArchive] = useState(null); // 'Celebrations', 'Internal Programs', 'External Programs', or null

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/events');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Filter only visible events
        const visibleEvents = data.data.filter(
          (event) => event.is_visible === 1
        );
        setAllEvents(visibleEvents);
      } else {
        setError(data.message || "Failed to fetch events");
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Error fetching events: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Map event category to main category
  const mapToMainCategory = (category) => {
    if (!category) return "Celebrations"; // Default to Celebrations if no category

    const categoryLower = category.toLowerCase();

    // Celebrations mappings
    if (
      categoryLower.includes("celebration") ||
      categoryLower.includes("ceremony") ||
      categoryLower === "arama poojawa" ||
      categoryLower === "dana program" ||
      categoryLower === "cultural event"
    ) {
      return "Celebrations";
    }

    // Internal Programs mappings
    if (
      categoryLower.includes("internal") ||
      categoryLower.includes("retreat") ||
      categoryLower === "meditation session" ||
      categoryLower === "dhamma talk"
    ) {
      return "Internal Programs";
    }

    // External Programs mappings
    if (
      categoryLower.includes("external") ||
      categoryLower === "conference" ||
      categoryLower === "workshop" ||
      categoryLower === "seminar" ||
      categoryLower === "educational program" ||
      categoryLower === "community service"
    ) {
      return "External Programs";
    }

    // Default fallback
    return "Celebrations";
  };

  // Group events by main category
  const categorizedEvents = useMemo(() => {
    const categories = {
      "Celebrations": [],
      "Internal Programs": [],
      "External Programs": []
    };

    allEvents.forEach(event => {
      const mainCategory = mapToMainCategory(event.category);
      categories[mainCategory].push(event);
    });

    // Sort events by date (most recent first)
    Object.keys(categories).forEach(category => {
      categories[category].sort((a, b) => {
        const dateA = new Date(a.start_date);
        const dateB = new Date(b.start_date);
        return dateB - dateA;
      });
    });

    return categories;
  }, [allEvents]);

  // Get active events (up to 5 per category) and archived events
  const getActiveAndArchivedEvents = (category) => {
    const events = categorizedEvents[category] || [];
    return {
      active: events.slice(0, 5),
      archived: events.slice(5)
    };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getEventImage = (event) => {
    if (event.images && event.images.length > 0) {
      const imagePath = `${import.meta.env.VITE_BASE_URL}/storage/app/public/${event.images[0]}`;
      return imagePath;
    }
    return null;
  };

  const EventCard = ({ event, className }) => {
    const eventImage = getEventImage(event);

    return (
      <div className={`event-card ${className}`}>
        <div className="event-frame">
          {eventImage ? (
            <img src={eventImage} alt={event.title} className="event-image" />
          ) : (
            <div className="no-image-placeholder">
              <span>No Image Available</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const CategorySection = ({ categoryName }) => {
    const { active, archived } = getActiveAndArchivedEvents(categoryName);
    const [currentSlide, setCurrentSlide] = useState(0);
    const hasArchived = archived.length > 0;

    // Reset slide when active events change
    useEffect(() => {
      if (active.length > 0 && currentSlide >= active.length) {
        setCurrentSlide(0);
      } else if (active.length === 0) {
        setCurrentSlide(0);
      }
    }, [active.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const nextSlide = () => {
      setCurrentSlide((prev) => (prev === active.length - 1 ? 0 : prev + 1));
    };

    const prevSlide = () => {
      setCurrentSlide((prev) => (prev === 0 ? active.length - 1 : prev - 1));
    };

    return (
      <div className="category-column">
        <div className="category-header">
          <h3 className="category-title">{categoryName}</h3>
        </div>
        <div className="category-slider-container">
          {active.length > 0 ? (
            <>
              <div className="slider-wrapper">
                <button
                  className="slider-arrow slider-arrow-left"
                  onClick={prevSlide}
                  disabled={active.length <= 1}
                  aria-label="Previous slide"
                >
                  &#8249;
                </button>
                <div className="slider-content">
                  {active.map((event, index) => (
                    <div
                      key={event.id}
                      className={`slider-slide ${index === currentSlide ? "slide-active" : "slide-hidden"
                        }`}
                    >
                      <EventCard event={event} className="flyer-card" />
                    </div>
                  ))}
                </div>
                <button
                  className="slider-arrow slider-arrow-right"
                  onClick={nextSlide}
                  disabled={active.length <= 1}
                  aria-label="Next slide"
                >
                  &#8250;
                </button>
              </div>
              {active.length > 1 && (
                <div className="slider-dots">
                  {active.map((_, index) => (
                    <button
                      key={index}
                      className={`slider-dot ${index === currentSlide ? "dot-active" : ""
                        }`}
                      onClick={() => setCurrentSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="no-flyers">No flyers available</div>
          )}
        </div>
      </div>
    );
  };

  const ArchiveButtons = () => {
    const categories = ["Celebrations", "Internal Programs", "External Programs"];

    // Map categories to images
    const categoryImages = {
      "Celebrations": barrelBronze,
      "Internal Programs": barrelBlack,
      "External Programs": barrelSilver
    };

    // Filter to only show categories that have archived items
    const categoriesWithArchives = categories.filter((categoryName) => {
      const { archived } = getActiveAndArchivedEvents(categoryName);
      return archived.length > 0;
    });

    // Don't render anything if no categories have archives
    if (categoriesWithArchives.length === 0) {
      return null;
    }

    return (
      <div className="archive-buttons-container">
        {categoriesWithArchives.map((categoryName) => {
          const { archived } = getActiveAndArchivedEvents(categoryName);

          return (
            <button
              key={categoryName}
              className="archive-barrel-button"
              onClick={() => setActiveArchive(activeArchive === categoryName ? null : categoryName)}
            >
              <div className="archive-barrel-wrapper">
                <img src={categoryImages[categoryName]} alt={`${categoryName} Archive`} className="barrel-image" />
                <div className="archive-text-overlay">
                  <span className="archive-text">Archive</span>
                  <span className="archive-count">({archived.length})</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const ArchiveModal = ({ categoryName, onClose }) => {
    const { archived } = getActiveAndArchivedEvents(categoryName);

    return (
      <div className="archive-modal-overlay" onClick={onClose}>
        <div className="archive-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="archive-modal-header">
            <h2>{categoryName} - Archive</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="archive-flyers-grid">
            {archived.length > 0 ? (
              archived.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  className="archive-flyer-card"
                />
              ))
            ) : (
              <div className="no-archived-flyers">No archived flyers</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="notification-page inside-pages">
      <div className="right-box">
        <img
          src={imageOne}
          alt="Right Top Image"
          className="right-image"
          width={300}
        />
      </div>

      <h1 className="font-main dark-gold p-4">Event Notifications</h1>

      <div className="test p-4">
        <div className="p-block font-p text-bold">
          <p>
            The Bhaddeka Vihari Monastery and Meditation Centre offers both
            internal and external Dhamma programs, along with Buddhist
            celebrations. Notifications for these programs are shared within the
            community and are also included in this announcement box. We invite
            you to select your special event and reserve the dates to join us.
          </p>
        </div>
      </div>

      <div className="p-4 mt-5">
        {loading && (
          <div className="loading-container">
            <p>Loading events...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p>Error: {error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="categories-container">
            <CategorySection categoryName="Celebrations" />
            <CategorySection categoryName="Internal Programs" />
            <CategorySection categoryName="External Programs" />
          </div>
        )}

        {!loading && !error && allEvents.length === 0 && (
          <div className="no-events-container">
            <p>No events available at the moment.</p>
          </div>
        )}
      </div>

      <div className="bottom-box mt-5">
        <img src={imageTwo} alt="bottom-image" width={400} />
      </div>



      {!loading && !error && (
        <div className="archive-section mt-5">
          <ArchiveButtons />
        </div>
      )}

      {activeArchive && (
        <ArchiveModal
          categoryName={activeArchive}
          onClose={() => setActiveArchive(null)}
        />
      )}

      <style jsx>{`
        .categories-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
          width: 100%;
          margin: 0 auto;
          padding: 0 20px;
        }

        .category-column {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .category-header {
          background: linear-gradient(135deg, #654321, #8b4513);
          padding: 15px;
          border-radius: 10px 10px 0 0;
          margin-bottom: 0;
        }

        .category-title {
          color: white;
          font-size: 1.2em;
          font-weight: bold;
          margin: 0;
          text-align: center;
        }

        .category-slider-container {
          padding: 15px;
          background: #f5f5dc;
          border: 2px solid #d4af37;
          border-top: none;
          border-radius: 0 0 10px 10px;
          position: relative;
        }

        .slider-wrapper {
          position: relative;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          width: 100%;
          min-height: 200px;
        }

        .slider-content {
          position: relative;
          width: 100%;
          overflow: hidden;
          display: block;
        }

        .slider-slide {
          width: 100%;
          opacity: 0;
          transition: opacity 0.5s ease-in-out;
          z-index: 1;
          pointer-events: none;
          position: absolute;
          top: 0;
          left: 0;
        }

        .slider-slide.slide-active {
          opacity: 1;
          z-index: 2;
          pointer-events: auto;
          position: relative;
          height: auto;
        }

        .slider-slide.slide-hidden {
          opacity: 0;
          z-index: 1;
          pointer-events: none;
        }

        .slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(139, 69, 19, 0.8);
          color: white;
          border: 2px solid #d4af37;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          font-size: 24px;
          font-weight: bold;
          cursor: pointer;
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .slider-arrow:hover:not(:disabled) {
          background: rgba(139, 69, 19, 1);
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
        }

        .slider-arrow:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .slider-arrow-left {
          left: 10px;
        }

        .slider-arrow-right {
          right: 10px;
        }

        .slider-dots {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-top: 15px;
          padding-top: 15px;
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid #8b4513;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot:hover {
          background: #d4af37;
          transform: scale(1.2);
        }

        .slider-dot.dot-active {
          background: #8b4513;
          transform: scale(1.3);
        }

        .flyer-card {
          width: 100%;
        }

        .event-card {
          width: 100%;
          cursor: pointer;
          position: relative;
          z-index: 1;
        }

        .event-frame {
          background: linear-gradient(135deg, #8b4513, #d2691e);
          border-radius: 15px;
          padding: 15px;
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          width: 100%;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .event-card:hover .event-frame {
          transform: scale(1.08);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
          z-index: 10;
        }

        .event-image {
          width: 100%;
          height: auto;
          max-width: 100%;
          object-fit: contain;
          border-radius: 10px;
          transition: transform 0.3s ease;
          display: block;
        }

        .event-card:hover .event-image {
          transform: scale(1.05);
        }

        .no-image-placeholder {
          width: 100%;
          min-height: 200px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-style: italic;
          font-size: 1.1em;
          border: 2px dashed rgba(255, 255, 255, 0.3);
        }

        .no-flyers {
          text-align: center;
          padding: 40px;
          color: #666;
          font-style: italic;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .archive-section {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 30px 20px;
          width: 100%;
        }

        .archive-buttons-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 40px;
          flex-wrap: wrap;
          width: 100%;
          max-width: 1200px;
        }

        .archive-barrel-button {
          background: none;
          border: none;
          cursor: pointer;
          transition: transform 0.3s ease;
          padding: 0;
        }

        .archive-barrel-button:hover {
          transform: translateY(-8px) scale(1.05);
        }

        .archive-barrel-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 200px;
          height: auto;
          transition: all 0.3s ease;
        }

        .barrel-image {
          width: 200px;
          height: 240px;
          object-fit: contain;
          filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.3));
        }

        .archive-text-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 80%;
          pointer-events: none;
        }

        .archive-text {
          color: white;
          font-size: 1.8em;
          font-weight: 500;
          font-family: 'Times New Roman', serif;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
          margin: 0;
          line-height: 1;
        }

        .archive-count {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1.0em;
          font-weight: bold;
          font-family: 'Times New Roman', serif;
          margin-top: 5px;
          text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
        }

        .archive-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .archive-modal-content {
          background: white;
          border-radius: 15px;
          padding: 30px;
          max-width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
          position: relative;
        }

        .archive-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #d4af37;
        }

        .archive-modal-header h2 {
          margin: 0;
          color: #654321;
          font-size: 1.5em;
        }

        .close-button {
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 50%;
          width: 35px;
          height: 35px;
          font-size: 1.5em;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .close-button:hover {
          background: #c82333;
          transform: rotate(90deg);
        }

        .archive-flyers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .archive-flyer-card {
          width: 100%;
        }

        .archive-flyer-card:hover .event-frame {
          transform: scale(1.08);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
          z-index: 10;
        }

        .archive-flyer-card:hover .event-image {
          transform: scale(1.05);
        }

        .no-archived-flyers {
          text-align: center;
          padding: 40px;
          color: #666;
          font-style: italic;
          grid-column: 1 / -1;
        }

        .loading-container,
        .error-container,
        .no-events-container {
          text-align: center;
          padding: 40px;
          font-size: 1.1em;
        }

        .error-container {
          color: #dc3545;
        }

        /* Responsive design */
        @media (max-width: 992px) {
          .categories-container {
            flex-direction: column;
            gap: 30px;
          }

          .category-column {
            width: 100%;
          }

          .archive-buttons-container {
            gap: 30px;
          }
        }

        @media (max-width: 768px) {
          .categories-container {
            padding: 0 10px;
          }

          .category-slider-container {
            padding: 10px;
          }

          .slider-wrapper {
            min-height: 150px;
          }

          .slider-arrow {
            width: 35px;
            height: 35px;
            font-size: 20px;
          }

          .slider-arrow-left {
            left: 5px;
          }

          .slider-arrow-right {
            right: 5px;
          }

          .archive-buttons-container {
            gap: 20px;
            padding: 20px 10px;
          }

          .archive-barrel {
            width: 100px;
            height: 120px;
          }

          .archive-barrel::before {
            width: 110px;
            height: 20px;
          }

          .archive-text {
            font-size: 1em;
            margin-top: 25px;
          }

          .archive-count {
            font-size: 0.85em;
          }

          .archive-flyers-grid {
            grid-template-columns: 1fr;
          }

          .archive-modal-content {
            padding: 20px;
            max-width: 95%;
          }
        }

        /* Large screens */
        @media (min-width: 1200px) {
          .categories-container {
            gap: 30px;
          }

          .archive-buttons-container {
            gap: 50px;
          }
        }
      `}</style>
    </div>
  );
};

export default EventNotification;
